import { Form, SelectProps } from 'antd';
import { FormProps } from 'antd/lib';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';

import useFormInitialSet from '@/hooks/useFormInitialSet';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
// Mejora: se extrajo la lógica duplicada de autocompletar empresa desde ticket a useTicketCompanyAutoFill
import { useTicketCompanyAutoFill } from '@/hooks/useTicketCompanyAutoFill';
import { RESOURCES } from '@/Resources';

export const useShipmentForm = (formProps: FormProps, initialValuesModified, edit?: boolean) => {
  const { form } = formProps;
  const { initialValuesSetted } = useFormInitialSet(formProps.form, initialValuesModified, [], {
    withoutInitial: !edit && !initialValuesModified?.ticket,
  });
  const { companySelectProps, technicianSelectProps, shippersSelectProps, ticketsSelectProps } =
    useOutletContext<{
      companySelectProps: SelectProps;
      shippersSelectProps: SelectProps;
      technicianSelectProps: SelectProps;
      ticketsSelectProps: SelectProps;
    }>();
  const companySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: companySelectProps,
    resource: 'empresas',
    initialValue:
      initialValuesModified?.company != initialValuesModified?.shipment_payment_company
        ? [initialValuesModified?.company, initialValuesModified?.shipment_payment_company]
        : initialValuesModified?.company,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const technicianSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: technicianSelectProps,
    resource: 'empresas',
    initialValue: initialValuesModified?.technician,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const shippersSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: shippersSelectProps,
    resource: RESOURCES.SHIPPERS,
    initialValue: initialValuesModified?.shipper,
    labelKey: 'name',
    valueKey: 'id',
  });
  const ticketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: ticketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: initialValuesModified?.ticket,
    labelKey: 'title',
    valueKey: 'id',
  });
  /**
   * Autocompletar empresa desde ticket (usa hook genérico).
   * El callback onTicketSelected setea campos adicionales específicos de Shipment.
   */
  const {
    ticketsSelectProps: ticketsSelectPropsModified,
    companySelectProps: companySelectPropsModified,
    movilLink,
  } = useTicketCompanyAutoFill({
    form,
    ticketsSelectProps: ticketsSelectPropsWithInitials,
    companySelectProps: companySelectPropsWithInitials,
    initialTicketId: initialValuesModified?.ticket,
    edit,
    onTicketSelected: companyOption => {
      // Campos adicionales de shipment: cuit y empresa de facturación
      form?.setFieldValue('shipment_cuit', companyOption.item?.cuit);
      form?.setFieldValue('shipment_payment_company', companyOption.value);
      form?.setFieldValue('shipment_payment_cuit', companyOption.item?.cuit);
      /**
       * metadata:
       * - feature: shipments.form-autofill-validation
       * - purpose: refrescar warnings visuales luego de autocompletar empresa sin escalar rechazos esperables a Sentry
       */
      void formProps.form
        ?.validateFields(['shipment_payment_company', 'shipment_payment_cuit'])
        .catch(() => undefined);
    },
  });
  /**
   *  Chequeo de bloqueo preventivo.
   */
  const [blockPreventive, setBlockPreventive] = useState(false);
  const selectedPaymentCompany = Form.useWatch('shipment_payment_company', form);
  const selectedCompany = Form.useWatch('company', form);

  useEffect(() => {
    if (selectedPaymentCompany || selectedCompany) {
      const selectedPaymentCompanyOption = companySelectPropsModified.options.find(
        option => option.value === selectedPaymentCompany,
      )?.item;
      const selectedCompanyOption = companySelectPropsModified.options.find(
        option => option.value === selectedCompany,
      )?.item;

      setBlockPreventive(
        !!selectedPaymentCompanyOption?.bloqueo_envio_preventivo ||
          !!selectedCompanyOption?.bloqueo_envio_preventivo,
      );
    } else {
      setBlockPreventive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentCompany, selectedCompany]);

  return {
    shippersSelectProps: shippersSelectPropsWithInitials,
    companySelectProps: companySelectPropsModified,
    ticketsSelectProps: ticketsSelectPropsModified,
    technicianSelectProps: technicianSelectPropsWithInitials,
    initialValuesSetted,
    blockPreventive,
    movilLink,
  };
};
